import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { CategoryWithPosts } from 'types/category';
import useStyles from './styles';
import { PostGrid } from 'organisms';
import { PageHeader } from 'molecules';
import { ScrollToTop, PaginationOptions, QueryResult, Seo } from '@elevator/ui';
import { DOMAIN_BASE_SHORT, ROUTES } from 'config/Nav';

/**
 * Category Component
 *
 * Displays a category page with posts in a grid layout, pagination support, and SEO metadata.
 * Handles display limit state for progressive loading and fetchMore for pagination.
 * Uses simplified pagination logic following the Tag pattern.
 *
 * Features:
 * - SEO metadata (title, description, canonical URL)
 * - PageHeader for the category title and PostGrid for posts with pagination
 * - Load More functionality (cache-first, then fetchMore)
 * - Bottom ad via `withLayout` (`bottomAd`)
 * - Scroll to top button
 *
 * Used in:
 * - Category layout index (rendered for category pages)
 *
 * @param props - Component props
 * @param props.category - Category data with posts
 * @param props.slug - Category slug identifier
 * @param props.variables - GraphQL query variables (slug and cursor)
 * @param props.fetchMore - Apollo Client fetchMore function for pagination
 * @param props.loading - Whether initial data is loading
 * @param props.loadingMore - Whether more posts are being fetched
 * @param props.error - GraphQL query error
 * @returns Category component with posts grid and pagination
 */
const Category = (props: CategoryPropTypes) => {
  const { classes } = useStyles();
  const { category, fetchMore, variables, slug, loading, loadingMore, error } = props;

  // Display limit state - resets when slug changes (adjust during render)
  const [displayLimit, setDisplayLimit] = useState(variables.cursor.limit);
  const [prevSlug, setPrevSlug] = useState(slug);
  const [revealAfterLength, setRevealAfterLength] = useState<number | null>(null);
  if (slug !== prevSlug) {
    setPrevSlug(slug);
    setDisplayLimit(variables.cursor.limit);
    setRevealAfterLength(null);
  }

  // Get full posts array from category (cache)
  const allPosts = category?.posts || [];
  // Slice posts array based on displayLimit
  const displayedPosts = allPosts.slice(0, displayLimit);

  // After fetchMore, reveal the next page once cache length grows
  if (revealAfterLength != null && allPosts.length > revealAfterLength) {
    setRevealAfterLength(null);
    setDisplayLimit((prev) => prev + variables.cursor.limit);
  }

  const onLoadMore = () => {
    // Check if more posts exist in cache
    if (allPosts.length > displayLimit) {
      // More posts available in cache - just increase display limit (instant)
      setDisplayLimit((prev) => prev + variables.cursor.limit);
    } else {
      // Need to fetch more posts; reveal once cache grows
      setRevealAfterLength(allPosts.length);
      fetchMore({
        variables: {
          cursor: {
            limit: variables.cursor.limit,
            skip: allPosts.length,
          },
        },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult || !prev.category) return prev;
          const newPosts = [
            ...(prev.category.posts || []),
            ...(fetchMoreResult.category?.posts || []),
          ];
          return {
            ...prev,
            category: {
              ...prev.category,
              posts: newPosts,
            },
          };
        },
      });
    }
  };

  return (
    <Stack
      className={classes.container}
      data-layout="category"
      component="main"
      spacing={2}
    >
      <Seo
        type={'website'}
        data={{
          baseUrl: DOMAIN_BASE_SHORT,
          title: category ? category.name : 'Category',
          path: ROUTES.CATEGORY.to + slug,
          description: `View ELEVATOR posts in the ${category?.name || ''} category.`,
        }}
      />
      <PageHeader
        title={category ? category.name : slug}
        titleSlot={
          loading && !category ? (
            <Skeleton
              variant="text"
              animation="wave"
              sx={(theme) => ({
                fontSize: theme.typography.h1.fontSize,
                lineHeight: theme.typography.h1.lineHeight,
                width: 'min(280px, 70%)',
              })}
            />
          ) : undefined
        }
        divider={true}
      />
      <Box component="section">
        <PostGrid
          posts={displayedPosts}
          onLoadMore={onLoadMore}
          loadLimit={variables.cursor.limit}
          loadingMore={loadingMore}
          error={error}
          isLoading={loading}
          adUnitPrefix="ELEVATOR-Mid"
        />
      </Box>
      <ScrollToTop visibleYOffset={70} />
    </Stack>
  );
};

export type CategoryQueryVariables = {
  slug: string;
  cursor: PaginationOptions;
};

export interface CategoryPropTypes extends QueryResult<
  'category',
  CategoryWithPosts,
  CategoryQueryVariables
> {
  slug: string;
  loadingMore?: boolean;
}

export default Category;
